import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Group, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';
import { openEraseModal } from '../EraseModal';
import { contactKeyOf } from '../ContactAvatar';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

/**
 * Opt-in, Remove and Erase: what can end a contact's place in the book.
 *
 * `onDeleted` rather than a navigation: this lives in a drawer over the list
 * now, so a delete closes the drawer and the list refreshes behind it. It used
 * to be a tab on its own page and pushed a route.
 */
export function DangerTab({ contact, onDeleted }: { contact: contacts.Contact; onDeleted: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  // The number, or the address of an email-only contact (4 October 2026).
  const who = contactKeyOf(contact);
  const emailOnly = !contact.msisdn;

  const toggleOptIn = useMutation({
    mutationFn: () =>
      contacts.upsertContact({
        msisdn: contact.msisdn ?? undefined,
        name: contact.name ?? undefined,
        email: contact.email ?? undefined,
        locale: contact.locale ?? undefined,
        tags: contact.tags,
        opt_in: !contact.opt_in
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact', contact.id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      notifySuccess(contact.opt_in ? t('contacts.view.danger.toast.markedOptOut') : t('contacts.view.danger.toast.markedOptIn'), t('toast.saved'));
    },
    onError: (err) => notifyError(err instanceof Error ? err.message : t('contacts.view.danger.toast.optInFailed'), t('toast.saveFailed'))
  });

  // Remove (2 October 2026): out of the list, opted out, the number kept.
  const del = useMutation({
    mutationFn: () => contacts.deleteContact(contact.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      notifySuccess(t('contacts.remove.toast', { msisdn: who }), t('contacts.remove.toastTitle'));
      onDeleted();
    },
    onError: (err) => notifyError(err instanceof Error ? err.message : t('contacts.remove.failed'), t('toast.deleteFailed'))
  });

  function confirmDelete() {
    openConfirmModal({
      title: t('contacts.remove.title', { msisdn: who }),
      children: (
        <Stack gap="xs">
          <Text fz="sm">{t('contacts.remove.body')}</Text>
          <Text fz="sm" c={MUTED}>{t('contacts.remove.back')}</Text>
        </Stack>
      ),
      labels: { confirm: t('contacts.remove.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => del.mutate()
    });
  }

  return (
    <Stack gap="md">
      {/* WhatsApp consent; an email-only contact has no number to consent
          with, and its email consent is on the Details tab. */}
      {!emailOnly && <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('contacts.columns.optIn')}</Title>
          <Text fz="sm" c={MUTED}>
            {t('contacts.view.danger.optInHint')}
          </Text>
          <Group>
            <Button
              size="xs"
              variant="outline"
              color={contact.opt_in ? 'red' : 'green'}
              loading={toggleOptIn.isPending}
              onClick={() => toggleOptIn.mutate()}
            >
              {contact.opt_in ? t('contacts.view.danger.markOptOut') : t('contacts.view.danger.markOptIn')}
            </Button>
          </Group>
        </Stack>
      </Card>}

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('contacts.remove.cardTitle')}</Title>
          <Text fz="sm" c={MUTED}>{emailOnly ? t('contacts.remove.cardHintEmail', { email: who }) : t('contacts.remove.cardHint', { msisdn: who })}</Text>
          <Group>
            <Button size="xs" color="red" variant="outline" loading={del.isPending} onClick={confirmDelete}>
              {t('contacts.remove.button')}
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Erase (2 October 2026): a POPIA request to delete the person's
          information, everywhere. Cannot be undone, so it asks for the
          number (or, for an email-only contact, the address) typed again. */}
      <Card withBorder style={{ borderColor: 'var(--rr-color-danger)' }}>
        <Stack gap="sm">
          <Title order={4} c="red">{t('contacts.erase.cardTitle')}</Title>
          <Alert color="red" variant="light">{t('contacts.erase.cardHint')}</Alert>
          <Group>
            <Button
              size="xs"
              color="red"
              onClick={() => openEraseModal({ title: t('contacts.erase.modal.title', { msisdn: who }), qc, contact, onErased: onDeleted })}
            >
              {t('contacts.erase.button')}
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
